import { SharingData } from '../../schema/SharingData';
import { shouldUseGenericError } from '../../selectors/sharingIssueDetectors/isPermissionNotSet';
import { SharingTipId } from '../../types/sharingTipId';
import { SharingTipRecipientInfo } from '../../types/SharingTipRecipientInfo';
import { assertNever } from 'owa-assert';
import { format } from 'owa-strings';
import strings from 'owa-strings/owa-mail-strings';

export interface InfoBarStringTemplates {
    singleFile: string;
    multipleFiles: string;
}

export function sharingTipIdToInfoBarString(
    sharingTipId: SharingTipId,
    recipientInfo: SharingTipRecipientInfo[],
    sharingData: SharingData[]
): InfoBarStringTemplates {
    switch (sharingTipId) {
        case SharingTipId.noPermissionToShare:
            return {
                singleFile: strings.NoPermissionToShareText,
                multipleFiles: strings.NoPermissionToShareText,
            };
        case SharingTipId.getSharingInfoFailed:
            return {
                singleFile: strings.NoSharingInfoText,
                multipleFiles: strings.NoSharingInfoText,
            };
        case SharingTipId.linkHasExpired:
            return {
                singleFile: strings.SingleLinkIsExpiredText,
                multipleFiles: strings.MultipleLinksAreExpiredText,
            };
        case SharingTipId.linkWillExpire:
            return {
                singleFile: strings.SingleLinkWillExpireText,
                multipleFiles: strings.MultipleLinksWillExpireText,
            };
        case SharingTipId.noPermissionToShareExternally:
            return {
                singleFile: strings.NoPermissionToShareExternallyText,
                multipleFiles: strings.NoPermissionToShareExternallyText,
            };
        case SharingTipId.externalRecipientCSL:
            return {
                singleFile: strings.WarningSharingCSLWithExternalRecipientsText,
                multipleFiles: strings.WarningSharingCSLWithExternalRecipientsText,
            };
        case SharingTipId.noPermissionSet:
            return {
                singleFile: getSingleFileStringFormatForIsPermissionNotSet(sharingData[0]),
                multipleFiles: strings.WarningSharingWithoutSettingPermissionsText,
            };
        case SharingTipId.sharingWithLargeDL:
            const bigDLNames: string = getBigDLNames(recipientInfo);
            return {
                singleFile: format(
                    strings.NoPermissionBigDLRecipientsText,
                    bigDLNames,
                    '{0}',
                    '{1}'
                ),
                multipleFiles: format(
                    strings.NoPermissionBigDLRecipientsText,
                    bigDLNames,
                    '{0}',
                    '{1}'
                ),
            };
        default:
            return assertNever(sharingTipId);
    }
}

const getBigDLNames = (recipientInfo: SharingTipRecipientInfo[] | null) => {
    if (recipientInfo) {
        const bigDLs = recipientInfo.filter(recipient => {
            return recipient.totalMemberCount > 100;
        });
        return bigDLs.map(DL => DL.name).join(', ');
    }
    return '';
};

function getSingleFileStringFormatForIsPermissionNotSet(sharingData: SharingData): string {
    if (shouldUseGenericError(sharingData.sharingInfo)) {
        return strings.WarningSharingWithoutSettingPermissionsText;
    }
    const totalRecipientCount = sharingData.sharingInfo.recipientPermissionInfos.filter(
        info => !info.hasAccess && info.getPermissionInfoStatus
    ).length;

    const recipientsNames = getAllRecipientsNames(sharingData);
    if (totalRecipientCount === 1) {
        return format(strings.SingleRecipientCannotAccessFileText, recipientsNames, '{0}');
    } else if (totalRecipientCount < 4) {
        return format(strings.MultipleRecipientsCannotAccessFileText, recipientsNames, '{0}');
    } else {
        return strings.WarningSharingWithoutSettingPermissionsText;
    }
}

function getAllRecipientsNames(sharingData: SharingData): string {
    return sharingData.sharingInfo.recipientPermissionInfos
        .filter(x => !x.hasAccess)
        .map(x => (x.name ? x.name : x.emailAddress))
        .join(', ');
}
